// The front door hero: a field of embers rises from below, converges into the
// Phoenix silhouette, settles, and stops. Hand-written 2D canvas, no
// dependencies — it runs once when the app is opened for the first time, so it
// must be cheap, quiet, and finite. It is decoration and nothing else: every
// word on the screen stands without it.
//
// The silhouette is the same grid the 8-bit mascot flies with (FRAMES[0] in
// pixel.ts). That module keeps its frames private, so the grid is copied here
// verbatim; if it ever gains an `export`, this copy should give way to it.

type Grid = readonly string[];

const SILHOUETTE: Grid = [
  "...................................",
  ".gg...........ro................gg.",
  ".oogg..........og.............ggoo.",
  ".rroogg........ggwgg........ggoorr.",
  ".drrroogg......gwweg......ggoorrrd.",
  ".kddrrooogg....gwwwgog..ggooorrddk.",
  "..dkdrrrooogg..gggggr.ggooorrrdkd..",
  "....ddrrroooogg.gwg.ggoooorrrdd....",
  ".....kddrrroooogwwwgoooorrrddk.....",
  "......dkdrrrrogwwwwwgorrrrdkd......",
  "........dddrrrgwwwwwgrrrddd........",
  ".........kdddrgwwwwwgrdddk.........",
  "..........dkddgwwwwwgddkd..........",
  "............ddgwwwwwgdd............",
  ".............kdgwwwgdk.............",
  "...............gwwwg...............",
  "...............ggggg...............",
  "..............g.ggg.g.....o........",
  ".....o.......o.o.ooo.o.............",
  "............r..o.ooo..r............",
  "...........d..o..oo.o..d.....o.....",
  "........o.....r..oo.r..............",
  ".............d...oo..d.............",
  ".................rr................",
];

const GRID_W = SILHOUETTE[0]!.length;
const GRID_H = SILHOUETTE.length;

/** Rows of empty sky kept under the bird so embers have somewhere to rise from. */
const SKY_ROWS = 4;

const DURATION_MS = 7000;
const TAU = Math.PI * 2;

// Heat level per sprite character: 0 is the deep outline ember, 3 is white-hot.
// Everything is mixed from --accent and --gold, so the field follows the theme.
const HEAT: Record<string, number> = { k: 0, d: 0, e: 0, r: 1, o: 1, g: 2, w: 3 };

type Rgb = [number, number, number];

const ACCENT_FALLBACK: Rgb = [0xc0, 0x86, 0x37];
const GOLD_FALLBACK: Rgb = [0xf0, 0xd0, 0x8a];

interface Ember {
  /** Target centre, css px, always on a whole cell. */
  tx: number;
  ty: number;
  sx: number;
  sy: number;
  level: number;
  /** Both are fractions of the whole run, so the timeline is resolution-free. */
  delay: number;
  span: number;
  wob: number;
  phase: number;
  spin: number;
}

/** Deterministic jitter — the same bird assembles the same way every time. */
function rnd(seed: number): number {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function parseColor(value: string, fallback: Rgb): Rgb {
  const v = value.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v);
  if (hex) {
    const h = hex[1]!;
    const full = h.length === 3 ? `${h[0]!}${h[0]!}${h[1]!}${h[1]!}${h[2]!}${h[2]!}` : h;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
  }
  const rgb = /(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(v);
  if (rgb) return [Number(rgb[1]!), Number(rgb[2]!), Number(rgb[3]!)];
  return fallback;
}

function mix(a: Rgb, b: Rgb, k: number): Rgb {
  return [
    Math.round(a[0] + (b[0] - a[0]) * k),
    Math.round(a[1] + (b[1] - a[1]) * k),
    Math.round(a[2] + (b[2] - a[2]) * k),
  ];
}

const css = (c: Rgb): string => `rgb(${c[0]},${c[1]},${c[2]})`;

/** A soft round glow, pre-rendered once per heat level — cheap to stamp. */
function glowSprite(c: Rgb, px: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const g = canvas.getContext("2d")!;
  const half = px / 2;
  const grad = g.createRadialGradient(half, half, 0, half, half, half);
  grad.addColorStop(0, `rgba(${c[0]},${c[1]},${c[2]},0.8)`);
  grad.addColorStop(0.4, `rgba(${c[0]},${c[1]},${c[2]},0.26)`);
  grad.addColorStop(1, `rgba(${c[0]},${c[1]},${c[2]},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, px, px);
  return canvas;
}

/**
 * Mounts the ember field into `host`. Runs about seven seconds and then stops
 * for good; pass `still` for the closing screen, which wants the settled frame
 * without the journey. Honours prefers-reduced-motion and page visibility.
 */
export function mountEmber(host: HTMLElement, size = 320, still = false): { dispose(): void } {
  const cell = Math.max(2, Math.floor(size / GRID_W));
  const w = cell * GRID_W;
  const h = cell * (GRID_H + SKY_ROWS);
  const spriteTop = cell * 2;

  const canvas = document.createElement("canvas");
  canvas.className = "ember-canvas";
  canvas.setAttribute("aria-hidden", "true");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  host.append(canvas);

  // A weak machine gets half the field: the shape still reads, the fan stays off.
  const stride = (navigator.hardwareConcurrency ?? 8) <= 4 ? 2 : 1;

  const embers: Ember[] = [];
  let seed = 0;
  for (let gy = 0; gy < GRID_H; gy++) {
    const row = SILHOUETTE[gy]!;
    for (let gx = 0; gx < row.length; gx++) {
      const level = HEAT[row[gx]!];
      if (level === undefined) continue;
      seed++;
      if (seed % stride !== 0) continue;
      const tx = gx * cell + cell / 2;
      const ty = gy * cell + spriteTop + cell / 2;
      // Lowest cells land first, so the bird builds upward out of the fire.
      const rise = 1 - gy / (GRID_H - 1);
      embers.push({
        tx,
        ty,
        sx: tx + (rnd(seed * 1.7) - 0.5) * w * 0.55,
        sy: h + cell * 2 + rnd(seed * 3.1 + 5) * h * 0.9,
        level,
        delay: rise * 0.34 + rnd(seed * 5.3 + 11) * 0.12,
        span: 0.42 + rnd(seed * 7.9 + 3) * 0.12,
        wob: cell * (0.8 + rnd(seed * 2.3 + 17) * 2.2),
        phase: rnd(seed * 4.1 + 29) * TAU,
        spin: 1.6 + rnd(seed * 6.7 + 41) * 2.4,
      });
    }
  }

  const glowPx = Math.min(48, Math.max(8, cell * 6));
  let cores: string[] = [];
  let glows: HTMLCanvasElement[] = [];

  function readPalette(): void {
    const cs = getComputedStyle(host);
    const accent = parseColor(cs.getPropertyValue("--accent"), ACCENT_FALLBACK);
    const gold = parseColor(cs.getPropertyValue("--gold"), GOLD_FALLBACK);
    const levels: Rgb[] = [
      accent,
      mix(accent, gold, 0.5),
      gold,
      mix(gold, [255, 255, 255], 0.4),
    ];
    cores = levels.map(css);
    glows = levels.map((c) => glowSprite(c, glowPx));
  }
  readPalette();

  function render(p: number): void {
    ctx.clearRect(0, 0, w, h);
    for (const e of embers) {
      const local = Math.min(1, Math.max(0, (p - e.delay) / e.span));
      if (local <= 0) continue;
      const rest = 1 - local;
      const k = 1 - rest * rest * rest; // ease-out cubic: fast rise, soft landing
      const settle = 1 - k;
      const wob = Math.sin(p * TAU * e.spin + e.phase) * e.wob * settle;
      const x = e.sx + (e.tx - e.sx) * k + wob;
      const y = e.sy + (e.ty - e.sy) * k;
      // Flicker fades out with the travel, so the final frame is exact.
      const flicker = 1 - 0.22 * settle * (0.5 + 0.5 * Math.sin(p * TAU * e.spin * 2 + e.phase));
      const alpha = Math.min(1, local * 3) * flicker;
      ctx.globalAlpha = alpha * 0.85;
      ctx.drawImage(glows[e.level]!, x - glowPx / 2, y - glowPx / 2, glowPx, glowPx);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = cores[e.level]!;
      ctx.fillRect(Math.round(x - cell / 2), Math.round(y - cell / 2), cell, cell);
    }
    ctx.globalAlpha = 1;
  }

  const quiet = still || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let raf = 0;
  let finished = quiet;
  let startedAt = performance.now();
  let hiddenAt = 0;

  function step(now: number): void {
    const p = Math.min(1, (now - startedAt) / DURATION_MS);
    render(p);
    if (p >= 1) {
      // Done: hold the still frame and let the loop end rather than idle at 60fps.
      finished = true;
      raf = 0;
      return;
    }
    raf = requestAnimationFrame(step);
  }

  if (quiet) render(1);
  else raf = requestAnimationFrame(step);

  // A backgrounded tab must not burn battery; coming back resumes where it was.
  function onVisibility(): void {
    if (document.hidden) {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
        hiddenAt = performance.now();
      }
    } else if (!finished && !raf) {
      startedAt += performance.now() - hiddenAt;
      raf = requestAnimationFrame(step);
    }
  }
  document.addEventListener("visibilitychange", onVisibility);

  const themeQuery = window.matchMedia("(prefers-color-scheme: dark)");
  function onTheme(): void {
    readPalette();
    if (finished) render(1);
  }
  themeQuery.addEventListener("change", onTheme);

  return {
    dispose(): void {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      finished = true;
      document.removeEventListener("visibilitychange", onVisibility);
      themeQuery.removeEventListener("change", onTheme);
      canvas.remove();
    },
  };
}
